import { motion } from "framer-motion";
import { Zap, Home, ArrowDown, ChevronRight } from "lucide-react";

const SOLAR_FARM_IMG = "https://images.unsplash.com/photo-1770936994282-8811fb7129ac?auto=format&fit=crop&q=80&w=960";
const SOLAR_ROOF_IMG = "https://images.unsplash.com/photo-1660330589505-9a433a742a7b?auto=format&fit=crop&q=80&w=960";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_solar-energy-savings/artifacts/4q9fuk03_SIMBOLO%20SOLISY.png";

export const ServiceSelector = () => {
  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section data-testid="service-selector" className="relative min-h-[100vh] flex flex-col items-center justify-center overflow-hidden" style={{ background: "#0c1b3a" }}>
      {/* Subtle grid pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")" }} />

      {/* Top branding */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="relative z-10 text-center mb-6 pt-24 md:pt-28"
      >
        <img src={LOGO_URL} alt="Solisy" className="h-16 md:h-20 w-auto mx-auto mb-4" />
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.1] mb-4 px-4">
          Energia solar do <span className="text-[#f5b731]">seu jeito</span>
        </h1>
        <p className="text-base md:text-lg text-white/60 max-w-2xl mx-auto px-4">
          Escolha a melhor forma de economizar com energia solar. Sem complicação, sem burocracia.
        </p>
      </motion.div>

      {/* Two option cards */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 w-full pb-20 md:pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">

          {/* Option 1: Geração Distribuída */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <button
              data-testid="option-geracao-distribuida"
              onClick={() => scrollTo("#geracao-distribuida")}
              className="group w-full text-left rounded-3xl overflow-hidden relative block cursor-pointer border-2 border-transparent hover:border-[#f5b731]/50 transition-colors duration-300"
              style={{ minHeight: "380px" }}
            >
              {/* Background image */}
              <img
                src={SOLAR_FARM_IMG}
                alt="Fazenda solar"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0c1b3a] via-[#0c1b3a]/70 to-transparent" />

              {/* Content */}
              <div className="relative z-10 flex flex-col justify-end h-full p-8 md:p-10" style={{ minHeight: "380px" }}>
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: "rgba(245, 183, 49, 0.2)", backdropFilter: "blur(8px)" }}>
                  <Zap className="w-7 h-7 text-[#f5b731]" />
                </div>
                <span className="inline-block px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[#f5b731]/20 text-[#f5b731] mb-3 w-fit">
                  Sem instalação
                </span>
                <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3 leading-tight">
                  Energia por Assinatura
                </h2>
                <p className="text-sm text-white/70 leading-relaxed mb-5 max-w-sm">
                  Economize de <strong className="text-white">20% a 30%</strong> na conta de luz sem instalar nada. 
                  Nossa fazenda solar gera e injeta os créditos direto na sua conta.
                </p>
                <div className="flex items-center gap-2 text-[#f5b731] text-sm font-semibold group-hover:gap-3 transition-all duration-300">
                  Quero economizar
                  <ChevronRight className="w-4 h-4" />
                </div>
              </div>
            </button>
          </motion.div>

          {/* Option 2: Instalação de Painéis */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
          >
            <button
              data-testid="option-instalacao"
              onClick={() => scrollTo("#instalacao")}
              className="group w-full text-left rounded-3xl overflow-hidden relative block cursor-pointer border-2 border-transparent hover:border-[#f5b731]/50 transition-colors duration-300"
              style={{ minHeight: "380px" }}
            >
              {/* Background image */}
              <img
                src={SOLAR_ROOF_IMG}
                alt="Instalação de painéis solares"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0c1b3a] via-[#0c1b3a]/70 to-transparent" />

              {/* Content */}
              <div className="relative z-10 flex flex-col justify-end h-full p-8 md:p-10" style={{ minHeight: "380px" }}>
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: "rgba(26, 79, 160, 0.3)", backdropFilter: "blur(8px)" }}>
                  <Home className="w-7 h-7 text-white" />
                </div>
                <span className="inline-block px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest bg-white/15 text-white mb-3 w-fit">
                  Na sua casa
                </span>
                <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3 leading-tight">
                  Painéis Solares no Telhado
                </h2>
                <p className="text-sm text-white/70 leading-relaxed mb-5 max-w-sm">
                  Instale painéis solares e <strong className="text-white">elimine sua conta de luz</strong>. 
                  Parcelas que cabem no bolso e possibilidade de vender a energia que sobrar.
                </p>
                <div className="flex items-center gap-2 text-[#f5b731] text-sm font-semibold group-hover:gap-3 transition-all duration-300">
                  Quero meus painéis
                  <ChevronRight className="w-4 h-4" />
                </div>
              </div>
            </button>
          </motion.div>

        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="flex justify-center mt-10"
        >
          <ArrowDown className="w-5 h-5 text-white/30 animate-bounce" />
        </motion.div>
      </div>
    </section>
  );
};
